/**
 * Énumération des types de contenu supporté
 *
 * **Attention: les valeurs à saisir dans le JSON doivent être en minuscule**
 */
import { ProductError, ProductErrorType } from './error';

export enum ContentType {
  /**
   * Le texte supporte en général le markdown
   */
  TEXT = 'text',
  TEXT_IMAGE = 'text-image',
  IMAGE_TEXT = 'image-text',
  IMAGE = 'image',
  AUDIO = 'audio',
  VIDEO = 'video',
  LINK = 'link',
  FILE = 'file',
}

/**
 * Représente un contenu.
 *
 * Peut être de type text, image, podcast ou vidéo.
 *
 * La plupart du temps, `content` est présenté dans une liste qui permet de composer plusieurs média, les uns après les autres.
 */
export class Content {
  type: ContentType;

  /**
   * le contenu au format text/markdown
   */
  content?: string;
  /**
   * une URL vers le contenu (pour les images, podcasts, vidéos et lien hypertext)
   */
  src?: string;

  title?: string;

  constructor(rawValue?: Content) {
    if (rawValue) {
      this.type = rawValue.type;
      this.content = rawValue.content;
      this.src = rawValue.src;
      this.title = rawValue.title;
    } else {
      this.type = null;
      this.content = null;
      this.src = null;
      this.title = null;
    }
  }

  static validate(content: Content, contentIndex: number, payload?: any): ProductError[] {
    const p = payload ? { ...payload, contentIndex } : { contentIndex };
    if (!content.type) {
      return [{ property: 'type', type: ProductErrorType.MISSING, payload: p }];
    }
    const errors: ProductError[] = [];
    const contentTypesWithTitle = [ContentType.LINK];
    const contentTypesWithText = [ContentType.TEXT, ContentType.TEXT_IMAGE, ContentType.IMAGE_TEXT];
    const contentTypesWithSrc = [
      ContentType.TEXT_IMAGE,
      ContentType.IMAGE_TEXT,
      ContentType.IMAGE,
      ContentType.VIDEO,
      ContentType.AUDIO,
    ];
    if (contentTypesWithText.includes(content.type) && !content.content) {
      errors.push({ property: 'content', type: ProductErrorType.MISSING, payload: p });
    }
    if (contentTypesWithSrc.includes(content.type) && !content.src) {
      errors.push({ property: 'src', type: ProductErrorType.MISSING, payload: p });
    }
    if (contentTypesWithTitle.includes(content.type) && !content.title) {
      errors.push({ property: 'title', type: ProductErrorType.MISSING, payload: p });
    }
    return errors;
  }
}

export class ContentValidationCriterion {
  type: ContentCriterionType;
  availableTimeInSeconds?: number;

  constructor(criterion?: ContentValidationCriterion) {
    this.type = criterion ? criterion.type : null;
    this.availableTimeInSeconds = criterion ? criterion.availableTimeInSeconds : null;
  }
}

export enum ContentCriterionType {
  CLICK_ALL = 'click-all',
  TIMER = 'timer',
}
